import { Outlet, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { AccountScreen } from "./pages/AccountScreen";
import { DashboardScreen } from "./pages/DashboardScreen";
import { TransporterOnboardingScreen } from "./pages/TransporterOnboardingScreen";
import { EarningsScreen } from "./pages/EarningsScreen";
import { FleetScreen } from "./pages/FleetScreen";
import { ManualDispatchScreen } from "./pages/ManualDispatchScreen";
import { RunsScreen } from "./pages/RunsScreen";
import SupportScreen from "./pages/SupportScreen";
import { TransporterRunDetailPage } from "./pages/TransporterRunDetailPage";
import { WorkspaceLayout } from "./workspace/WorkspaceLayout";

const rootRoute = createRootRoute({
  component: function RootLayout() {
    return (
      <div className="min-h-screen bg-td-page">
        <Outlet />
        {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-left" /> : null}
      </div>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/workspace/dashboard" });
  },
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspace",
  component: WorkspaceLayout,
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/workspace/dashboard" });
  },
});

const workspaceDashboardRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "dashboard",
  validateSearch: (raw: Record<string, unknown>): { q?: string } => {
    const q = raw.q;
    if (typeof q !== "string" || q.trim().length === 0) return {};
    return { q: q.trim() };
  },
  component: DashboardScreen,
});

const workspaceFleetRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "fleet",
  component: FleetScreen,
});

const workspaceRunsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "runs",
  component: RunsScreen,
});

const workspaceRunDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "run/$runId",
  component: TransporterRunDetailPage,
});

const workspaceManualDispatchRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "manual-dispatch",
  component: ManualDispatchScreen,
});

const workspaceEarningsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "earnings",
  component: EarningsScreen,
});

const workspaceAccountRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "account",
  validateSearch: (raw: Record<string, unknown>): { tab: "org" | "payout" | "team" } => {
    const t = raw.tab;
    if (t === "payout" || t === "team" || t === "org") return { tab: t };
    return { tab: "org" };
  },
  component: AccountScreen,
});

const workspaceOnboardingRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "onboarding",
  component: TransporterOnboardingScreen,
});

const workspaceSupportRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "support",
  component: SupportScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  workspaceRoute.addChildren([
    workspaceIndexRoute,
    workspaceDashboardRoute,
    workspaceOnboardingRoute,
    workspaceFleetRoute,
    workspaceRunsRoute,
    workspaceRunDetailRoute,
    workspaceManualDispatchRoute,
    workspaceEarningsRoute,
    workspaceAccountRoute,
    workspaceSupportRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
